import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import EditServiceUserForm from '../components/EditServiceUserForm';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

export default function ServiceUserDetailsPage() {
    const { serverId, serviceUserId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [serviceUser, setServiceUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('personal');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const { userRole } = useAuth();
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');

    // 1. Listen for Edit Signal
    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    useEffect(() => {
        async function fetchServiceUser() {
            try {
                const docRef = doc(db, 'servers', serverId, 'serviceUsers', serviceUserId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    // We must manually mix the ID (docSnap.id) into the data object
                    setServiceUser({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.error("No such service user!");
                }
            } catch (error) {
                console.error("Error fetching service user:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchServiceUser();
    }, [serverId, serviceUserId]);

    if (loading) return <div style={{ padding: 20 }}>Loading Profile...</div>;
    if (!serviceUser) return <div style={{ padding: 20 }}>Service User not found.</div>;

    // Helper: Safely access nested objects
    const addressData = serviceUser.address || {};
    const gpData = serviceUser.gpInfo || {};
    const pharmacyData = serviceUser.pharmacyInfo || {};
    const socialWorkerData = serviceUser.socialWorker || {};
    const nextOfKinData = serviceUser.nextOfKin || {};
    const fundingData = serviceUser.fundingBody || {};

    const Field = ({ label, value }) => (
        <div style={{ marginBottom: '15px', minWidth: '200px', flex: '1' }}>
            <label style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#888', marginBottom: '4px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{label}</label>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', wordBreak: 'break-word' }}>{value || '-'}</div>
        </div>
    );

    const Section = ({ title, children }) => (
        <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '25px', 
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #f0f2f5', paddingBottom: '10px', fontSize: '16px' }}>{title}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {children}
            </div>
        </div>
    );

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <img src={serviceUser.profilePicture || 'https://via.placeholder.com/150'} alt="Profile" style={{ width: '64px', height: '64px', borderRadius: '50%', marginRight: '20px', objectFit: 'cover', border: '2px solid #eee' }} />
                <div>
                    <h1 style={{ margin: 0, fontSize: '22px', color: '#2c3e50' }}>{serviceUser.fullName}</h1>
                    <p style={{ margin: '5px 0 0', color: '#666' }}>ID: {serviceUser.serviceUserID || 'N/A'} • {serviceUser.type || 'Service User'}</p>
                </div>
                {canEdit && (<button onClick={() => setIsEditModalOpen(true)} style={{ marginLeft: 'auto', padding: '10px 20px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Edit Profile</button>)}
            </div>

            {/* Navigation Tabs */}
            <div style={{ display: 'flex', gap: '5px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {['personal', 'contact', 'care & medical', 'professional contacts', 'billing'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', textTransform: 'capitalize', fontWeight: '500',
                            backgroundColor: activeTab === tab ? '#0056b3' : 'transparent',
                            color: activeTab === tab ? 'white' : '#555'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* --- TAB CONTENT --- */}

            {activeTab === 'personal' && (
                <Section title="Personal Information">
                    <Field label="Full Name" value={serviceUser.fullName} />
                    <Field label="Known As" value={serviceUser.knownAs} />
                    <Field label="Date of Birth" value={serviceUser.dob} />
                    <Field label="Gender" value={serviceUser.gender} />
                    <Field label="Nationality" value={serviceUser.nationality} />
                    <Field label="Ethnicity" value={serviceUser.ethnicity} />
                    <Field label="Religion" value={serviceUser.religion} />
                    <Field label="Marital Status" value={serviceUser.maritalStatus} />
                    <Field label="Sexual Orientation" value={serviceUser.sexualOrientation} />
                    <Field label="Preferred Language" value={serviceUser.preferredLanguage} />
                    <Field label="NI Number" value={serviceUser.niNumber} />
                    <Field label="NHS Number" value={serviceUser.nhsNumber} />
                </Section>
            )}

            {activeTab === 'contact' && (
                <>
                <Section title="Address Details">
                    <Field label="Flat / Building" value={addressData.secondLineOfAddress} />
                    <Field label="Street / First Line" value={addressData.firstLineOfAddress} />
                    <Field label="Town / City" value={addressData.town} />
                    <Field label="County" value={addressData.county} />
                    <Field label="Postcode" value={addressData.postalCode} />
                    <Field label="Country" value={addressData.country} />
                    <Field label="Access Keycode" value={serviceUser.keycode} />
                </Section>
                <Section title="Contact Info">
                    <Field label="Email" value={serviceUser.email} />
                    <Field label="Primary Phone" value={`${serviceUser.phoneCode || ''} ${serviceUser.phoneNumber || ''}`} />
                    <Field label="Secondary Phone" value={serviceUser.secondaryTel} />
                </Section>
                </>
            )}

            {activeTab === 'care & medical' && (
                <>
                <Section title="Medical Overview">
                    <Field label="Mental State" value={serviceUser.mentalState} />
                    <Field label="DNACPR Status" value={serviceUser.dnacpr} />
                    <Field label="Care Requirement" value={serviceUser.careRequirementLevelTag} />
                </Section>
                <Section title="GP Information">
                    <Field label="GP Name" value={gpData.gpName} />
                    {/* Add GP Phone/Address if available in data later */}
                </Section>
                <Section title="Pharmacy Information">
                    <Field label="Pharmacy Name" value={pharmacyData.pharmacyName} />
                </Section>
                </>
            )}

            {activeTab === 'professional contacts' && (
                <>
                <Section title="Social Worker">
                    <Field label="Name" value={socialWorkerData.name} />
                    <Field label="Organisation" value={socialWorkerData.organisation} />
                    <Field label="Email" value={socialWorkerData.email} />
                    <Field label="Phone" value={socialWorkerData.phone} />
                    <Field label="Emergency Contact" value={socialWorkerData.emergencyContact} />
                </Section>
                <Section title="Next of Kin">
                    <Field label="Name" value={nextOfKinData.name} />
                    <Field label="Relation" value={nextOfKinData.relation} />
                    <Field label="Phone" value={nextOfKinData.phone} />
                    <Field label="Email" value={nextOfKinData.email} />
                    <Field label="Address" value={nextOfKinData.address} />
                </Section>
                <Section title="Power of Attorney (POA)">
                    {serviceUser.poa && serviceUser.poa.length > 0 ? (
                        serviceUser.poa.map((p, index) => (
                            <div key={index} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px dashed #eee', width: '100%' }}>
                                <strong>{p.name}</strong> - {p.type}
                            </div>
                        ))
                    ) : (
                        <div>No POA recorded.</div>
                    )}
                </Section>
                </>
            )}

            {activeTab === 'billing' && (
                <Section title="Funding & Charges">
                    <Field label="Funding Type" value={fundingData.fundingType} />
                    <Field label="Funding Agreed?" value={serviceUser.fundingAgreedTag} />
                    <div style={{ width: '100%', height: '1px', background: '#eee', margin: '15px 0' }}></div>
                    <Field label="Primary Hourly Rate" value={`£${serviceUser.charge?.primaryHourlyCharge || 0}`} />
                    <Field label="Bank Holiday Rate" value={`£${serviceUser.charge?.bankHolidayHourlyCharge || 0}`} />
                    <Field label="Mileage Charge" value={`£${serviceUser.charge?.mileageCharge || 0} / mile`} />
                    <Field label="Invoice Frequency" value={serviceUser.charge?.primaryFrequency} />
                </Section>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
                <EditServiceUserForm 
                    serviceUser={serviceUser}
                    serverId={serverId}
                    closeModal={() => setIsEditModalOpen(false)}
                    onUpdate={() => window.location.reload()}
                />
            )}
        </div>
    );
}